/**
 * The fixed 12-colour palette every person is assigned a colour from.
 *
 * This is the single source of truth for colour *names* (they appear in the
 * admin colour picker and in changelog entries such as
 * `Person geändert — Farbe „Rosa“`) and for the colour values themselves.
 *
 * The same values are declared as CSS custom properties in
 * `client/src/styles/index.css`, because Tailwind's `@theme` block needs
 * literal values. `palette.test.ts` asserts that both stay in sync.
 */

export interface PaletteColor {
  /** 1-based index stored on a person. */
  index: number;
  /** German colour name shown in the UI. */
  name: string;
  /** Full-strength colour. */
  value: string;
  /** Text colour that is legible on top of {@link value}. */
  foreground: string;
}

export const PALETTE: readonly PaletteColor[] = [
  { index: 1, name: 'Blau', value: 'oklch(0.62 0.17 255)', foreground: '#ffffff' },
  { index: 2, name: 'Orange', value: 'oklch(0.72 0.15 60)', foreground: '#43230a' },
  { index: 3, name: 'Violett', value: 'oklch(0.60 0.17 300)', foreground: '#ffffff' },
  { index: 4, name: 'Petrol', value: 'oklch(0.58 0.11 195)', foreground: '#ffffff' },
  { index: 5, name: 'Gelb', value: 'oklch(0.79 0.14 90)', foreground: '#3d2f06' },
  { index: 6, name: 'Rosa', value: 'oklch(0.66 0.16 350)', foreground: '#ffffff' },
  { index: 7, name: 'Grün', value: 'oklch(0.62 0.14 150)', foreground: '#ffffff' },
  { index: 8, name: 'Indigo', value: 'oklch(0.52 0.16 275)', foreground: '#ffffff' },
  { index: 9, name: 'Rot', value: 'oklch(0.60 0.19 25)', foreground: '#ffffff' },
  { index: 10, name: 'Cyan', value: 'oklch(0.70 0.12 220)', foreground: '#0c2b33' },
  { index: 11, name: 'Limette', value: 'oklch(0.74 0.16 125)', foreground: '#233608' },
  { index: 12, name: 'Braun', value: 'oklch(0.55 0.09 60)', foreground: '#ffffff' },
] as const;

export const PALETTE_SIZE = PALETTE.length;

export function paletteColor(index: number): PaletteColor | undefined {
  return PALETTE[index - 1];
}

export function paletteColorName(index: number): string {
  return paletteColor(index)?.name ?? `Farbe ${index}`;
}

export function isValidColorIndex(index: number): boolean {
  return Number.isInteger(index) && index >= 1 && index <= PALETTE_SIZE;
}

/**
 * Colour suggested for a newly created person: the first palette entry that is
 * not taken yet, falling back to the first colour once all twelve are in use
 * (multiple assignment is allowed, the UI only warns about it).
 */
export function suggestColorIndex(takenIndices: readonly number[]): number {
  const taken = new Set(takenIndices);
  for (const color of PALETTE) {
    if (!taken.has(color.index)) return color.index;
  }
  return 1;
}
